import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import { Button, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { useConfirmationDialogContext } from "../../../../../../confirmation-dialog/ConfirmationDialogContext";
import { MissingFieldsContext, scopeMissingFields } from "../../../../../../forms/missing-fields/MissingFieldsContext";
import { nameof } from "../../../../../../utils/nameof";
import { IncomeSectionCard } from "../../../income/components/income-section-card/IncomeSectionCard";
import { AssetOwnClaimsFormValues, OwnClaimFormValue } from "../../OwnClaimsView.types";
import { createEmptyOwnClaim } from "../../OwnClaimsView.utils";
import { OwnClaimDialog } from "./components/own-claim-dialog/OwnClaimDialog";
import { OwnClaimsTable } from "./components/own-claims-table/OwnClaimsTable";

const TAB = "clients.client.debtSettlement.assetInventory.ownClaimsTab";
const SECTION = `${TAB}.list`;

interface Props {
  formik: FormikProps<AssetOwnClaimsFormValues>;
}

export function OwnClaimsListSection({ formik }: Props) {
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const [dialogClaim, setDialogClaim] = useState<OwnClaimFormValue | null>(null);
  const missingFields = useContext(MissingFieldsContext);

  const claims = formik.values.ownClaims;
  const fieldName = nameof<AssetOwnClaimsFormValues>("ownClaims");

  const addClaim = () => setDialogClaim(createEmptyOwnClaim(crypto.randomUUID()));

  const editClaim = (key: string) => setDialogClaim(claims.find((claim) => claim.key === key) ?? null);

  const saveClaim = (claim: OwnClaimFormValue) => {
    const exists = claims.some((other) => other.key === claim.key);
    void formik.setFieldValue(
      fieldName,
      exists ? claims.map((other) => (other.key === claim.key ? claim : other)) : [...claims, claim]
    );
  };

  const deleteClaim = (claim: OwnClaimFormValue, number: number) =>
    showConfirmationDialog({
      dialogTitleText: t(`${TAB}.detail.deleteConfirmTitle`),
      dialogText: t(`${TAB}.detail.deleteConfirmText`, { number }),
      agreeText: t("common.yes"),
      discardText: t("common.no"),
      onClose: (confirmed) => {
        if (!confirmed) {
          return;
        }
        setDialogClaim(null);
        void formik.setFieldValue(
          fieldName,
          claims.filter((other) => other.key !== claim.key)
        );
      },
    });

  const dialogIndex = dialogClaim ? claims.findIndex((claim) => claim.key === dialogClaim.key) : -1;
  const dialogNumber = (dialogIndex >= 0 ? dialogIndex : claims.length) + 1;

  const missingFieldsOf = (index: number) => scopeMissingFields(missingFields, `${fieldName}.${index}.`);
  const incompleteKeys = new Set(
    claims.filter((_, index) => missingFieldsOf(index).size > 0).map((claim) => claim.key)
  );

  return (
    <IncomeSectionCard title={t(`${SECTION}.title`)}>
      <Typography variant="body2">{t(`${SECTION}.intro`)}</Typography>

      {claims.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t(`${SECTION}.empty`)}
        </Typography>
      ) : (
        <OwnClaimsTable claims={claims} incompleteKeys={incompleteKeys} onEdit={editClaim} />
      )}

      {dialogClaim && (
        <MissingFieldsContext.Provider value={missingFieldsOf(dialogIndex)}>
          <OwnClaimDialog
            key={dialogClaim.key}
            claim={dialogClaim}
            number={dialogNumber}
            onSave={saveClaim}
            onDelete={dialogIndex >= 0 ? () => deleteClaim(dialogClaim, dialogNumber) : undefined}
            onClose={() => setDialogClaim(null)}
          />
        </MissingFieldsContext.Provider>
      )}

      <Stack direction="row" justifyContent={"end"}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addClaim} disabled={formik.isSubmitting}>
          {t(claims.length === 0 ? `${SECTION}.addFirst` : `${SECTION}.addFurther`)}
        </Button>
      </Stack>
    </IncomeSectionCard>
  );
}
